import React, { useState } from 'react';
import { Textarea } from '../catalyst/textarea';
import { Button } from '../catalyst/button';
import { sendMessage } from '../lib/api';

type Mode = 'nudge' | 'manual';

interface ChatInputProps {
	userId: string;
	onSent: () => void;
}

const ChatInput: React.FC<ChatInputProps> = ({ userId, onSent }) => {
	const [mode, setMode] = useState<Mode>('nudge');
	const [message, setMessage] = useState('');
	const [sending, setSending] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleSend = async () => {
		if (!message.trim()) return;
		setSending(true);
		setError(null);
		try {
			await sendMessage(userId, message.trim(), mode === 'nudge' ? 'inbound' : 'outbound');
			setMessage('');
			onSent();
		} catch (err: unknown) {
			setError(err instanceof Error ? err.message : 'Failed to send message');
		} finally {
			setSending(false);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSend();
		}
	};

	return (
		<div className="space-y-2">
			<div className="flex gap-1">
				<button
					onClick={() => setMode('nudge')}
					className={`rounded-md px-3 py-1 text-sm font-medium transition ${
						mode === 'nudge'
							? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
							: 'text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800'
					}`}
				>
					Nudge LLM
				</button>
				<button
					onClick={() => setMode('manual')}
					className={`rounded-md px-3 py-1 text-sm font-medium transition ${
						mode === 'manual'
							? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
							: 'text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800'
					}`}
				>
					Manual Reply
				</button>
			</div>
			<div className="flex gap-2">
				<div className="flex-1">
					<Textarea
						value={message}
						onChange={(e) => setMessage(e.target.value)}
						onKeyDown={handleKeyDown}
						placeholder={
							mode === 'nudge'
								? 'Send a message as the user to the LLM…'
								: 'Send a WhatsApp message directly to the user…'
						}
						resizable={false}
						rows={2}
						disabled={sending}
					/>
				</div>
				<Button
					color="emerald"
					onClick={handleSend}
					disabled={sending || !message.trim()}
				>
					{sending ? 'Sending…' : 'Send'}
				</Button>
			</div>
			{error && (
				<p className="text-sm text-red-600 dark:text-red-400">{error}</p>
			)}
			<p className="text-xs text-zinc-400">
				{mode === 'nudge'
					? 'Sends the message to the LLM as if from the user, then forwards the response to WhatsApp.'
					: 'Sends the message directly to the user on WhatsApp (bypasses LLM).'}
			</p>
		</div>
	);
};

export default ChatInput;
